/*
  TMS wishlist.

  Saved product handles live in localStorage under "tms-wishlist" (newest first, max 100).
  Every change fires a "tms:wishlist-change" event on document (it bubbles to window) and
  refreshes all hearts ([data-wishlist-toggle]) and header bubbles ([data-wishlist-count]).
  Other open tabs follow through the storage event.

  Optional sync: when snippets/wishlist-config.liquid is filled in and the customer is logged
  in, the page defines window.tmsWishlist = { api, cid, sig }. The list is then merged with the
  copy saved in the Cloudflare Worker on load and sent back after every change.

  The wishlist page (sections/main-wishlist.liquid, [data-wishlist-page]) is also driven here.
*/
(function () {
  'use strict';

  if (window.TMSWishlist) return;

  var KEY = 'tms-wishlist';
  var SYNC_KEY = 'tms-wishlist-sync';
  var EVENT = 'tms:wishlist-change';
  var MAX = 100;
  var HANDLE_RE = /^[a-z0-9-]{1,120}$/;

  var memory = [];

  /* ---------- storage ---------- */

  function clean(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length && out.length < MAX; i++) {
      var h = list[i];
      if (typeof h === 'string' && HANDLE_RE.test(h) && out.indexOf(h) === -1) out.push(h);
    }
    return out;
  }

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      memory = raw ? clean(JSON.parse(raw)) : [];
    } catch (e) {
      /* Storage blocked or bad JSON: keep the in-memory copy for this page view. */
    }
    return memory.slice();
  }

  function save(list) {
    memory = clean(list);
    try {
      window.localStorage.setItem(KEY, JSON.stringify(memory));
    } catch (e) {}
    return memory.slice();
  }

  function same(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  /* ---------- public API ---------- */

  function has(handle) {
    return read().indexOf(handle) !== -1;
  }

  function set(list, fromRemote) {
    var before = read();
    var after = save(list);
    if (same(before, after)) return after;
    if (!fromRemote) markDirty();
    changed();
    if (!fromRemote) scheduleSync();
    return after;
  }

  function add(handle) {
    if (!HANDLE_RE.test(handle || '')) return read();
    var list = read();
    if (list.indexOf(handle) !== -1) return list;
    list.unshift(handle);
    if (list.length > MAX) list.length = MAX;
    return set(list);
  }

  function remove(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i === -1) return list;
    list.splice(i, 1);
    return set(list);
  }

  function toggle(handle) {
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle);
    return true;
  }

  function changed() {
    var list = memory.slice();
    render();
    try {
      document.dispatchEvent(new CustomEvent(EVENT, { bubbles: true, detail: { items: list, count: list.length } }));
    } catch (e) {}
  }

  /* ---------- hearts and header bubble ---------- */

  function render() {
    var list = memory;
    var buttons = document.querySelectorAll('[data-wishlist-toggle]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var saved = list.indexOf(btn.getAttribute('data-handle')) !== -1;
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var text = btn.querySelector('[data-wishlist-text]');
      if (text) {
        text.textContent = saved ? 'Saved to wishlist' : 'Add to wishlist';
      } else {
        btn.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
      }
    }

    var count = list.length;
    var bubbles = document.querySelectorAll('[data-wishlist-count]');
    for (var j = 0; j < bubbles.length; j++) {
      var bubble = bubbles[j];
      var num = bubble.querySelector('[data-wishlist-count-number]');
      var label = bubble.querySelector('[data-wishlist-count-label]');
      if (num) num.textContent = count > 99 ? '99+' : String(count);
      if (label) label.textContent = count === 1 ? '1 item in wishlist' : count + ' items in wishlist';
      if (count > 0) bubble.removeAttribute('hidden');
      else bubble.setAttribute('hidden', '');
    }
  }

  var renderQueued = false;
  function queueRender() {
    if (renderQueued) return;
    renderQueued = true;
    (window.requestAnimationFrame || setTimeout)(function () {
      renderQueued = false;
      render();
    });
  }

  /* Capture phase so the card link or any slider never sees the click. */
  document.addEventListener(
    'click',
    function (e) {
      var target = e.target;
      var btn = target && target.closest ? target.closest('[data-wishlist-toggle]') : null;
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var nowSaved = toggle(btn.getAttribute('data-handle'));
      if (nowSaved) {
        btn.classList.remove('is-popping');
        void btn.offsetWidth;
        btn.classList.add('is-popping');
        setTimeout(function () {
          btn.classList.remove('is-popping');
        }, 400);
      }
    },
    true
  );

  window.addEventListener('storage', function (e) {
    if (e.key !== KEY && e.key !== null) return;
    read();
    changed();
  });

  /* Cards added later (filters, quick add, recommendations) get the right state too. */
  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (
            node.hasAttribute('data-wishlist-toggle') ||
            node.hasAttribute('data-wishlist-count') ||
            node.querySelector('[data-wishlist-toggle], [data-wishlist-count]')
          ) {
            queueRender();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---------- optional Cloudflare sync ---------- */

  var cfg = window.tmsWishlist;
  var syncOn = !!(cfg && typeof cfg.api === 'string' && cfg.api && cfg.cid && cfg.sig);
  var syncReady = false;
  var syncTimer = null;

  function readSyncState() {
    try {
      var s = JSON.parse(window.localStorage.getItem(SYNC_KEY) || 'null');
      return s && typeof s === 'object' ? s : {};
    } catch (e) {
      return {};
    }
  }

  function writeSyncState(dirty) {
    if (!syncOn) return;
    try {
      window.localStorage.setItem(SYNC_KEY, JSON.stringify({ cid: String(cfg.cid), dirty: !!dirty }));
    } catch (e) {}
  }

  function markDirty() {
    if (syncOn) writeSyncState(true);
  }

  function endpoint() {
    return (
      cfg.api.replace(/\/+$/, '') +
      '/wishlist?cid=' +
      encodeURIComponent(cfg.cid) +
      '&sig=' +
      encodeURIComponent(cfg.sig)
    );
  }

  function push() {
    syncTimer = null;
    if (!syncOn || !syncReady) return;
    fetch(endpoint(), {
      method: 'PUT',
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(read())
    })
      .then(function (r) {
        if (r.ok) writeSyncState(false);
      })
      .catch(function () {});
  }

  function scheduleSync() {
    if (!syncOn || !syncReady) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(push, 600);
  }

  function pull() {
    fetch(endpoint(), { method: 'GET', mode: 'cors', credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('wishlist sync ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var remote = clean(data && data.items);
        var local = read();
        var state = readSyncState();
        var merged;
        if (state.cid === String(cfg.cid) && !state.dirty) {
          /* This browser was already in step with the account: the saved copy wins,
             so removals made on another device stick. */
          merged = remote;
        } else {
          /* First sync here, or changes made while offline: keep both. */
          merged = clean(local.concat(remote));
        }
        set(merged, true);
        syncReady = true;
        if (same(merged, remote)) writeSyncState(false);
        else push();
      })
      .catch(function () {
        /* Worker unreachable: carry on with the local list, try again next page view. */
      });
  }

  if (syncOn) {
    window.addEventListener('pagehide', function () {
      if (syncTimer) {
        clearTimeout(syncTimer);
        push();
      }
    });
  }

  /* ---------- wishlist page ---------- */

  function formatMoney(cents, format) {
    var value = (Number(cents) || 0) / 100;
    function withDelims(number, decimals, thousands, decimal) {
      var parts = number.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }
    format = format || '${{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, kind) {
      switch (kind) {
        case 'amount_no_decimals':
          return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelims(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelims(value, 2, "'", '.');
        default:
          return withDelims(value, 2, ',', '.');
      }
    });
  }

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function sizedImage(src, width) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  var HEART_PATH =
    'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z';

  function heartButton(handle) {
    var btn = el('button', 'tms-wish');
    btn.type = 'button';
    btn.setAttribute('data-wishlist-toggle', '');
    btn.setAttribute('data-handle', handle);
    btn.setAttribute('aria-pressed', 'true');
    btn.setAttribute('aria-label', 'Remove from wishlist');
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('class', 'tms-wish__icon');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(ns, 'path');
    path.setAttribute('d', HEART_PATH);
    svg.appendChild(path);
    btn.appendChild(svg);
    return btn;
  }

  function initPage(page) {
    var grid = page.querySelector('[data-wishlist-grid]');
    var empty = page.querySelector('[data-wishlist-empty]');
    var loading = page.querySelector('[data-wishlist-loading]');
    var status = page.querySelector('[data-wishlist-status]');
    var moneyFormat = page.getAttribute('data-money-format') || '${{amount}}';
    var cartUrl = page.getAttribute('data-cart-url') || rootUrl() + 'cart';
    var cache = {}; /* handle -> product JSON, or false while loading */
    var failed = {}; /* handles that hit a network error this page view */

    function say(msg) {
      if (status) status.textContent = msg || '';
    }

    function card(handle, product) {
      var url = product.url || rootUrl() + 'products/' + handle;
      var variant = null;
      for (var i = 0; i < (product.variants || []).length; i++) {
        if (product.variants[i].available) {
          variant = product.variants[i];
          break;
        }
      }
      var price = variant ? variant.price : product.price;
      var compare = variant ? variant.compare_at_price : product.compare_at_price;

      var li = el('li', 'tms-wl__item');
      li.setAttribute('data-handle', handle);
      var box = el('div', 'tms-wl__card');

      var media = el('a', 'tms-wl__media');
      media.href = url;
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');
      if (product.featured_image) {
        var img = el('img');
        img.src = sizedImage(product.featured_image, 533);
        img.alt = '';
        img.loading = 'lazy';
        img.width = 533;
        img.height = 533;
        media.appendChild(img);
      }
      box.appendChild(media);
      box.appendChild(heartButton(handle));

      var info = el('div', 'tms-wl__info');
      var title = el('a', 'tms-wl__title', product.title || handle);
      title.href = url;
      info.appendChild(title);

      var priceBox = el('div', 'tms-wl__price');
      if (compare && compare > price) {
        var was = el('s', 'tms-wl__compare', formatMoney(compare, moneyFormat));
        priceBox.appendChild(was);
        priceBox.appendChild(document.createTextNode(' '));
      }
      priceBox.appendChild(document.createTextNode(formatMoney(price, moneyFormat)));
      info.appendChild(priceBox);

      var actions = el('div', 'tms-wl__actions');
      if (variant) {
        var addBtn = el('button', 'tms-wl__add', 'Add to cart');
        addBtn.type = 'button';
        addBtn.setAttribute('data-wishlist-add', String(variant.id));
        actions.appendChild(addBtn);
      } else {
        actions.appendChild(el('span', 'tms-wl__soldout', 'Sold out'));
      }
      var view = el('a', 'tms-wl__view', 'View');
      view.href = url;
      actions.appendChild(view);
      info.appendChild(actions);

      box.appendChild(info);
      li.appendChild(box);
      return li;
    }

    function draw() {
      var list = read();
      var waiting = false;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < list.length; i++) {
        var p = cache[list[i]];
        if (p) frag.appendChild(card(list[i], p));
        else if (!failed[list[i]]) waiting = true;
      }
      grid.innerHTML = '';
      grid.appendChild(frag);
      render();
      if (loading) loading.hidden = !waiting || list.length === 0;
      if (empty) empty.hidden = list.length > 0;
      grid.hidden = list.length === 0;
    }

    function load(handle) {
      cache[handle] = false;
      return fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (r) {
          if (r.status === 404) return null;
          if (!r.ok) throw new Error('product ' + r.status);
          return r.json();
        })
        .then(function (product) {
          if (product) {
            cache[handle] = product;
          } else {
            /* Product deleted or unpublished: drop it quietly. */
            delete cache[handle];
            remove(handle);
          }
        })
        .catch(function () {
          /* Network trouble: keep it saved, just leave it out this time. */
          delete cache[handle];
          failed[handle] = true;
        });
    }

    function refresh() {
      var list = read();
      var missing = [];
      for (var i = 0; i < list.length; i++) {
        if (!(list[i] in cache) && !failed[list[i]]) missing.push(list[i]);
      }
      draw();
      if (!missing.length) {
        if (loading) loading.hidden = true;
        return;
      }
      /* A few at a time so a long list does not flood the store. */
      var next = 0;
      function worker() {
        if (next >= missing.length) return Promise.resolve();
        var h = missing[next++];
        return load(h).then(worker);
      }
      var workers = [];
      for (var w = 0; w < Math.min(6, missing.length); w++) workers.push(worker());
      Promise.all(workers).then(function () {
        draw();
        if (loading) loading.hidden = true;
        var anyFailed = false;
        for (var k in failed) if (failed.hasOwnProperty(k)) anyFailed = true;
        if (anyFailed) say('Some items could not be loaded. Refresh the page to try again.');
      });
    }

    grid.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-wishlist-add]') : null;
      if (!btn || btn.disabled) return;
      e.preventDefault();
      btn.disabled = true;
      btn.textContent = 'Adding';
      say('');
      fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.getAttribute('data-wishlist-add')), quantity: 1 }] })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) throw new Error((data && (data.description || data.message)) || 'Could not add to cart');
            window.location.href = cartUrl;
          });
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = 'Add to cart';
          say((err && err.message) || 'Could not add to cart. Please try again.');
        });
    });

    document.addEventListener(EVENT, refresh);
    refresh();
  }

  /* ---------- start ---------- */

  window.TMSWishlist = {
    get: function () {
      return read();
    },
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    count: function () {
      return read().length;
    },
    formatMoney: formatMoney
  };

  function start() {
    read();
    render();
    var pages = document.querySelectorAll('[data-wishlist-page]');
    for (var i = 0; i < pages.length; i++) initPage(pages[i]);
    if (syncOn) pull();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
